import type { BayesianProbe } from '../types/engagement'
import type { EmbedStep, ProactivePlan, WindowStage } from '../types/snak2'

export const snak2Images = {
  window: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/ce84687e-affe-4826-ad25-8042021aeade.jpg',
    alt: 'Macro photograph of a human eye with a steel-blue iris',
  },
  fundus: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/b9bc1f9f-2347-41d2-84b8-2948cb2d568e.jpg',
    alt: 'Colour fundus photograph of a retina showing the optic disc, vessels and macula',
  },
}

export const bayesProbe: BayesianProbe = {
  trigger: 'Your prior?',
  priorLabel: 'Step 1 · state your prior',
  priorQuestion:
    'Before any data: in eyes with DME for a year or more, how often do you expect the outer retinal bands to be interrupted?',
  priorOptions: [
    { id: 'rare', label: 'Rarely — fluid dominates the picture' },
    { id: 'some', label: 'In a minority of eyes' },
    { id: 'often', label: 'In a substantial share of eyes' },
  ],
  evidenceLabel: 'Step 2 · the evidence',
  evidenceStat: 'Two of four',
  evidenceText:
    'Two of the four phenotypes identified across 2,355 eyes carry EZ or ELM disruption as part of their signature — and they are precisely the eyes where fluid volume alone under-describes what is happening.',
  posteriorLabel: 'Step 3 · update',
  posteriorQuestion: 'Knowing that, how often would you check band continuity at review?',
  posteriorOptions: [
    { id: 'same', label: 'As I do now', share: 12 },
    { id: 'more', label: 'More often than I do now', share: 46 },
    { id: 'every', label: 'At every DME review', share: 42 },
  ],
  closing:
    'Prior, evidence, updated view. That is what the phenotype adds — it does not tell you what to do, it changes what you weigh.',
  reference:
    'Peer shares are illustrative engagement data. Cluster signatures: Midena E, et al. AI-assisted OCT biomarker analysis, 2,355 DME eyes.',
}

export const snak2Rail = {
  chapter: 'Snak 2',
  eyebrow: 'Developing · embed precision · brand colours, no logo',
  title: ['Mind the', 'window.'],
  blurb:
    'For the specialist who already accepts that DME has phenotypes. Snak 2 adds the time axis — when the window closes — and makes phenotyping part of the routine.',
}

/* ── Screen 1 · The window ─────────────────────────────────────────── */

export const windowOfOpportunity = {
  eyebrow: 'Developing · embed precision',
  headline: ['The window closes', 'quietly.'],
  lead: 'Fluid is what we measure. Time is what we lose. Outer retinal structure changes while visual acuity still looks acceptable — so the window rarely announces itself.',
  scaleLabel: 'Outer retinal integrity over sustained exposure',
  scaleNote: 'Illustrative. Not a measured trajectory or a staging system.',
  takeaway: 'Phenotype is not only what. It is when.',
  cta: 'What does not come back',
}

export const windowStages: WindowStage[] = [
  {
    label: 'Oedema present',
    structure: 'ELM and EZ continuous',
    integrity: 96,
    functionNote: 'Acuity may be near-normal',
  },
  {
    label: 'Sustained exposure',
    structure: 'Foci accumulate, bands still traceable',
    integrity: 78,
    functionNote: 'Acuity often unchanged',
  },
  {
    label: 'Bands thinning',
    structure: 'First interruptions in the EZ',
    integrity: 52,
    functionNote: 'Acuity starts to lag',
  },
  {
    label: 'Disruption established',
    structure: 'ELM and EZ discontinuous',
    integrity: 24,
    functionNote: 'Recovery becomes uncertain',
  },
]

/* ── Screen 2 · Irreversibility ────────────────────────────────────── */

export const irreversible = {
  headline: 'Some of it does not come back.',
  lead: 'The two halves of a DME scan behave very differently over time. One is a volume problem. The other is an architecture problem.',
  reversibleLabel: 'Responds to treatment',
  reversibleItems: ['Intraretinal fluid volume', 'Subretinal fluid', 'Central retinal thickness'],
  irreversibleLabel: 'Not reliably recovered',
  irreversibleItems: ['ELM continuity', 'EZ continuity', 'Photoreceptor architecture'],
  compareEarly: 'c2',
  compareLate: 'c4',
  compareEarlyLabel: 'Bands still continuous',
  compareLateLabel: 'Bands interrupted',
  takeaway: 'Treat the fluid. Read the bands.',
  cta: 'Managing the clusters proactively',
}

/* ── Screen 3 · Proactive management ───────────────────────────────── */

export const proactive = {
  headline: ['Four clusters.', 'Four review rhythms.'],
  lead: 'Recognising the phenotype changes little on its own. Attaching a review rhythm to it is what makes it proactive.',
  watchLabel: 'Watch',
  intervalLabel: 'Rhythm',
  triggerLabel: 'Act when',
  caveat:
    'Illustrative review rhythms for discussion. Intervals and triggers remain a matter of local clinical practice and individual judgement.',
  cta: 'Fitting it into the clinic',
}

export const proactivePlans: ProactivePlan[] = [
  {
    clusterId: 'c1',
    watch: 'Central IRF volume · band continuity',
    interval: 'Review at 3 months',
    trigger: 'First foci appear, or any new SRF',
  },
  {
    clusterId: 'c2',
    watch: 'I-HRF burden · peripheral spread of IRF',
    interval: 'Review at 2–3 months',
    trigger: 'Rising foci despite stable fluid',
  },
  {
    clusterId: 'c3',
    watch: 'EZ continuity · persistence of SRF',
    interval: 'Review at 6–8 weeks',
    trigger: 'Any further interruption of the bands',
  },
  {
    clusterId: 'c4',
    watch: 'Extent of ELM/EZ loss · response to current therapy',
    interval: 'Review at 4–6 weeks',
    trigger: 'Plateau at review — reassess which driver remains',
  },
]

/* ── Screen 4 · Embed in the routine ──────────────────────────────── */

export const embed = {
  headline: 'Where it fits in the read.',
  lead: 'No new equipment, no additional scan, no extra visit. Four habits inside the read you already perform.',
  tuesdayLabel: 'What changes on Tuesday',
  tuesdayLine: 'Nothing in your equipment. One line in your notes, and one date in the diary.',
  cta: 'Where treatment enters',
}

export const embedSteps: EmbedStep[] = [
  {
    title: 'On the scan you already open',
    detail: 'The same B-scan and volume you review today — no additional acquisition.',
  },
  {
    title: 'Read five variables, not one',
    detail: 'IRF · SRF · I-HRF · ELM · EZ, in the order you already scan the retina.',
  },
  {
    title: 'Name the phenotype in the record',
    detail: 'One line. It makes the next visit a comparison rather than a fresh impression.',
  },
  {
    title: 'Set the interval to the phenotype',
    detail: 'The review rhythm follows the cluster instead of the calendar.',
  },
]

/* ── Screen 5 · Differentiation tail · Prevent ─────────────────────── */

export const prevent = {
  label: 'Differentiation · Developing',
  headline: ['Prevent, rather than', 'retrieve.'],
  lead: 'If the inflammatory arm is what the phenotype is showing, the argument is about timing: address it while the architecture is still intact.',
  productName: 'Ozurdex®',
  productDescriptor: 'dexamethasone intravitreal implant 0.7 mg',
  productLine:
    'A sustained-release intravitreal corticosteroid, indicated for adult patients with visual impairment due to diabetic macular oedema.',
  points: [
    'Considered where an inflammatory component is suspected',
    'The timing question sits before structural loss, not after it',
    'Monitoring for intraocular pressure and cataract progression applies',
  ],
  spc: 'Consult the Summary of Product Characteristics before prescribing. Report adverse events in line with national requirements.',
  internalNote:
    'Internal: efficacy, comparative and outcome claims to be supplied and approved by medical and regulatory. Brand colours only at this phase — no logo lockup.',
  ctaLabel: 'Book a proactive-management case review',
  ctaDetail: '30 minutes · your own scans · peer-to-peer',
  forwardEyebrow: 'Snak 3 · Advanced · ignite precision',
  forwardLine: 'For clinics already practising this: piloting AI, and predicting response.',
}

/* ── Shared ────────────────────────────────────────────────────────── */

export const snak2References = {
  window:
    'Structural-versus-functional lag discussed in the morphological OCT literature. Trajectory shown for illustration only.',
  irreversible:
    'Cluster examples: Midena E, et al. AI-assisted OCT biomarker analysis, 2,355 DME eyes.',
  proactive:
    'Review rhythms are illustrative discussion material, not a guideline or validated protocol.',
  embed: 'Workflow suggestion only. Local documentation practice applies.',
  prevent:
    'Ozurdex® (dexamethasone intravitreal implant). Prescribing information and full safety data in the Summary of Product Characteristics.',
}

export const snak2Disclaimer =
  'Phase 2 · Developing archetype. Brand colours applied without logo. Morphological phenotype clusters are not treatment groups, treatment-response categories or a validated severity staging system. Review rhythms and integrity trajectories shown are illustrative. Consult the Summary of Product Characteristics before prescribing.'
